import { PortalPageHeader } from "@/components/portal-page-header";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { classIdParamsSchema, organizationIdParamsSchema } from "@/lib/route-params";
import { useParentClassUpdatesGetClass, useParentClassUpdatesGetClassUpdates } from "@/orval/parent-class-updates";
import { createFileRoute, Outlet, useRouterState } from "@tanstack/react-router";
import { format, parse } from "date-fns";
import { ParentUpdateResults } from "./-components/parent-update-list";
import type { ParentClassUpdateClassDTO } from "@/orval/model";

const paramsSchema = organizationIdParamsSchema.merge(classIdParamsSchema);
const timeParseBaseDate = new Date(0);

function formatClassTime(time: string) {
  return format(parse(time, "HH:mm:ss", timeParseBaseDate), "h:mm a");
}

export const Route = createFileRoute("/_app/organizations/$organizationId/parent/classes_/$classId")({
  params: {
    parse: (params) => paramsSchema.parse(params),
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { organizationId, classId } = Route.useParams();
  const isViewingUpdate = useRouterState({
    select: (state) => state.location.pathname.includes(`/parent/classes/${classId}/updates/`),
  });
  const { data: classDetail, isLoading } = useParentClassUpdatesGetClass(organizationId, classId);
  const { data: updates, isLoading: isUpdatesLoading } = useParentClassUpdatesGetClassUpdates(organizationId, classId, {
    pageIndex: 0,
    pageSize: 25,
  });

  return (
    <div className="flex min-w-0 flex-1 flex-col p-4 md:p-6">
      {isViewingUpdate ? (
        <Outlet />
      ) : (
        <>
          <PortalPageHeader
            className="mb-4"
            breadcrumbs={[
              { label: "Classes", to: "/organizations/$organizationId/parent/classes", params: { organizationId } },
              { label: classDetail?.name ?? "Class" },
            ]}
            title={classDetail?.name ?? "Class"}
          />

          <ClassOverview classDetail={classDetail} isLoading={isLoading} />

          <h2 className="mb-3 text-xl font-semibold">Updates</h2>
          <ParentUpdateResults
            organizationId={organizationId}
            isLoading={isUpdatesLoading}
            updates={updates?.items}
            emptyMessage="No updates for this class yet."
          />
        </>
      )}
    </div>
  );
}

function ClassOverview({ classDetail, isLoading }: { classDetail?: ParentClassUpdateClassDTO; isLoading: boolean }) {
  if (isLoading) {
    return <Skeleton className="mb-6 h-32 w-full" />;
  }
  if (!classDetail) {
    return null;
  }
  return (
    <Card className="mb-6">
      <CardContent className="grid gap-4 p-4 md:grid-cols-[8rem_1fr]">
        {classDetail.imageUrl ? (
          <img src={classDetail.imageUrl} alt="" className="h-28 w-full rounded-md object-cover md:h-24" />
        ) : (
          <div className="h-28 rounded-md bg-muted md:h-24" />
        )}
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline">{classDetail.semesterName}</Badge>
            <Badge variant="secondary">
              {classDetail.classDayName}, {formatClassTime(classDetail.startTime)}-
              {formatClassTime(classDetail.endTime)}
            </Badge>
          </div>
          <div className="text-sm text-muted-foreground">Teacher: {classDetail.teacherName || "TBD"}</div>
          {classDetail.studentNames.length ? (
            <div className="text-sm text-muted-foreground">For: {classDetail.studentNames.join(", ")}</div>
          ) : null}
          {classDetail.description ? <p className="text-sm">{classDetail.description}</p> : null}
        </div>
      </CardContent>
    </Card>
  );
}
